// @ts-check

import { SSE } from "sse.js"
import z from "zod"
import * as Sentry from "@sentry/react"
import { SESSION_TIMEOUT_EVENT } from "@minohealth/common/services/ApiService"
import {
  ErrorCodes,
  ChatProviderError,
  parseBackendError,
  handleFetchError,
  createError
} from "./errorCodes.js"

// @ts-ignore
const baseUrl = import.meta.env.VITE_SOCKET_URL

/**
 * @param {number} status - HTTP response status code
 */
function dispatchSessionTimeoutIfUnauthorized(status) {
  if (status !== 401) return
  document.dispatchEvent(new CustomEvent(SESSION_TIMEOUT_EVENT, {}))
}


const GetChatResourcesSchema = z.object({
  chatSessions: z.array(z.object({}).passthrough()),
  projects: z.array(z.object({}).passthrough()),
  reports: z.array(z.object({}).passthrough()),
  chatImages: z.array(z.object({}).passthrough()),
  activeStreams: z.array(z.string())
})



/**
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function getChatResources(userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL("chat/resources", baseUrl), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatResourcesSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'getChatResources API call',
        errorCode: chatError.code,
        userToken: userToken ? 'present' : 'missing',
        signal: signal ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const GetChatSessionsSchema = z.object({
  sessions: z.array(z.object({}).passthrough()),
  thumbnails: z.array(
    z.object({
      sessionId: z.string(),
      image: z.object({}).passthrough(),
    })
  ),
  nextCursor: z.string().nullable()
})

/**
 * @param {string} userToken
 * @param {Object} [options]
 * @param {string} [options.projectId]
 * @param {number} [options.pageSize]
 * @param {string} [options.cursor]
 * @param {AbortSignal} [options.signal]
 */
async function getChatSessions(userToken, { projectId, pageSize, cursor, signal } = {}) {
  const url = new URL("chat/sessions", baseUrl)
  if(projectId != null) url.searchParams.append("projectId", projectId)
    if(pageSize != null) url.searchParams.append("page_size", pageSize + "")
    if(cursor != null) url.searchParams.append("cursor", cursor)
 
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatSessionsSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'getChatSessions API call',
        errorCode: chatError.code,
        projectId,
        pageSize,
        cursor,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const GetChatSessionSchema = z.object({
  session: z.object({}).passthrough()
})

/**
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function getChatSession(sessionId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}`, baseUrl), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if(!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatSessionSchema.parse(responseJson.data)
  } catch(error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: { 
        context: 'getChatSession API call',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const GetChatProjectsSchema = z.object({
  projects: z.array(z.object({}).passthrough())
})

/**
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function getChatProjects(userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL("chat/projects", baseUrl), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if(!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatProjectsSchema.parse(responseJson.data)
  } catch(error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: { 
        context: 'getChatProjects API call',
        errorCode: chatError.code,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const GetChatReportsSchema = z.object({
  reports: z.array(z.object({}).passthrough())
})

/**
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function getChatSessionReports(sessionId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}/reports`, baseUrl), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatReportsSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'getChatSessionReports API call',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const GetChatImagesSchema = z.object({
  images: z.array(z.object({}).passthrough())
})

/**
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function getChatSessionImages(sessionId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}/images`, baseUrl), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatImagesSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'getChatSessionImages API call',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const GetChatSessionMessagesSchema = z.object({
  messages: z.array(z.object({}).passthrough()),
  totalCount: z.number(),
  hasMore: z.boolean(),
})

/**
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {number} [options.limit]
 * @param {number} [options.offset]
 * @param {AbortSignal} [options.signal]
 */
async function getChatSessionMessages(sessionId, userToken, { limit = 50, offset = 0, signal } = {}) {
  try {
    const url = new URL(`chat/sessions/${sessionId}/messages`, baseUrl)
    url.searchParams.set('limit', String(limit))
    url.searchParams.set('offset', String(offset))

    const response = await fetch(url, {
      method: "GET",
      headers: { "Authorization": `Bearer ${userToken}` },
      signal,
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return GetChatSessionMessagesSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'getChatSessionMessages API call',
        errorCode: chatError.code,
        sessionId,
        offset,
        limit,
      }
    })
    throw chatError
  }
}

const CreateChatSessionSchema = z.object({
  session: z.object({}).passthrough()
})

/**
 * @param {string} userToken
 * @param {Object} [data]
 * @param {string} [data.projectId]
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function createChatSession(userToken, data = {}, { signal } = {}) {
  try {
    const response = await fetch(new URL("chat/sessions", baseUrl), {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return CreateChatSessionSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'createChatSession API call',
        errorCode: chatError.code,
        data: data ? 'present' : 'missing',
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const CreateNewProjectSchema = z.object({
  project: z.object({}).passthrough()
})

/**
 * @param {Object} data
 * @param {string} data.title
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function createChatProject(data, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL("chat/projects", baseUrl), {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return CreateNewProjectSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'createChatProject API call',
        errorCode: chatError.code,
        data: data ? 'present' : 'missing',
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const DeleteChatProjectSchema = z.object({
  projectId: z.string()
})

/**
 * @param {string} projectId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function deleteChatProject(projectId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/projects/${projectId}`, baseUrl), {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return DeleteChatProjectSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'deleteChatProject API call',
        errorCode: chatError.code,
        projectId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const UpdateChatSessionSchema = z.object({
  session: z.object({}).passthrough()
})

/**
 * @param {string} sessionId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function updateChatSession(sessionId, data, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}`, baseUrl), {
      method: "PUT",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return UpdateChatSessionSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'updateChatSession API call',
        errorCode: chatError.code,
        sessionId,
        data: data ? 'present' : 'missing',
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const DeleteChatSessionSchema = z.object({
  sessionId: z.string()
})

/**
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function deleteChatSession(sessionId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}`, baseUrl), {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return DeleteChatSessionSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'deleteChatSession API call',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const StreamEvents = /** @type {const} */ ({
  STREAM_STARTED: "stream-started",
  STREAM_CONTENT: "stream-content",
  STREAM_STRUCTURED_CONTENT: "stream-structured-content",
  STREAM_ENDED: "stream-ended",
})

const StreamStartedEventDataSchema = z.object({
  sessionId: z.string(),
  user: z.string(),
  meta: z.optional(
    z.object({}).passthrough()
  )
})
const StreamContentEventDataSchema = z.object({
  sessionId: z.string(),
  chunk: z.object({
    type: z.string(),
    data: z.object({
      content: z.string(),
    }),
  }),
})
const StreamStructuredContentEventDataSchema = z.object({
  sessionId: z.string(),
  chunk: z.object({
    type: z.string(),
    data: z.object({
      content: z.object({}).passthrough(),
    }),
  }),
})
const StreamEndedEventDataSchema = z.object({
  sessionId: z.string(),
  images: z.array(z.object({}).passthrough()),
  files: z.array(z.object({}).passthrough()),
  reports: z.array(z.object({}).passthrough()),
  messages: z.array(z.object({}).passthrough()),
})

const StreamEventsSchemaMap = /** @type {const} */ ({
  [StreamEvents.STREAM_STARTED]: StreamStartedEventDataSchema,
  [StreamEvents.STREAM_CONTENT]: StreamContentEventDataSchema,
  [StreamEvents.STREAM_STRUCTURED_CONTENT]: StreamStructuredContentEventDataSchema,
  [StreamEvents.STREAM_ENDED]: StreamEndedEventDataSchema,
})

/** @typedef {{[K in keyof typeof StreamEventsSchemaMap]: {event: K, data: z.infer<typeof StreamEventsSchemaMap[K]>}}[keyof typeof StreamEventsSchemaMap]} StreamEventsPayload */

class SSEStreamDisconnectedError extends ChatProviderError {
  constructor(message = "Stream disconnected") {
    super(ErrorCodes.STREAM_DISCONNECTED, message, "The SSE stream was disconnected");
    this.name = "SSEStreamDisconnectedError"
  }
}

/**
 * Helper function to create SSE streams for various endpoints
 * @param {string} url
 * @param {Object} options
 * @param {string} [options.method]
 * @param {import("sse.js").SSEHeaders} [options.headers]
 * @param {any} [options.payload]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function createSSEStream(url, { method = "POST", headers = {}, payload, signal } = {}) {
  const source = new SSE(url, {
    method,
    headers,
    payload: payload ? JSON.stringify(payload) : undefined,
  })
  signal?.addEventListener("abort", () => source.close())

  /** @type {ReadableStream<StreamEventsPayload>} */
  const readable = new ReadableStream({
    start(controller) {
      for (const eventType of Object.keys(StreamEventsSchemaMap)) {
        // @ts-ignore
        source.addEventListener(eventType, (/** @type {import("sse.js").SSEvent} */ event) => {
          try {
            // @ts-ignore
            const schema = StreamEventsSchemaMap[eventType]
            const data = schema.parse(JSON.parse(event.data))
            controller.enqueue({
              // @ts-ignore
              event: eventType,
              data,
            })
          } catch (err) {
            console.warn("Failed to parse SSE stream event data", err)
            Sentry.captureException(err, {
              extra: {
                context: 'SSE stream event parsing',
                eventType,
                url,
                method
              }
            });
          }
        })
      }

      source.addEventListener("error", (/** @type {import("sse.js").SSEvent} */ event) => {
        const isDisconnectionError = event.data === ""

        if (isDisconnectionError) {
          const disconnectError = new SSEStreamDisconnectedError()
          Sentry.captureException(disconnectError, {
            extra: {
              context: "SSE stream disconnection",
              url,
              method,
            }
          })
          controller.error(disconnectError)
          return
        }

        dispatchSessionTimeoutIfUnauthorized(event.responseCode)

        try {
          const eventData = JSON.parse(event.data)
          const streamError = eventData?.error
            ? new ChatProviderError(
              eventData.error.code || ErrorCodes.STREAM_ERROR,
              eventData.error.message || "Stream error occurred",
              eventData.error.details,
              eventData
            )
            : new ChatProviderError(
              ErrorCodes.STREAM_ERROR,
              eventData?.message || "Stream error occurred",
              undefined,
              eventData
            )

          Sentry.captureException(streamError, {
            extra: {
              context: "SSE stream error event",
              errorCode: streamError.code,
              url,
              method,
            }
          })
          controller.error(streamError)
        }
        catch (error) {
          const parseError = new ChatProviderError(
            ErrorCodes.STREAM_ERROR,
            "Failed to parse stream error",
            error.message,
            error
          )
          Sentry.captureException(parseError, {
            extra: {
              context: 'SSE stream error parsing',
              url,
              method,
              eventData: event.data,
              readyState: event.source.readyState,
            }
          });
          console.error(error)
          controller.error(parseError)
        }
      })
    },
    cancel() {
      source.close()
    }
  })

  return readable
}

/**
 * Generate a chat message (SSE stream)
 * @param {string} sessionId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function generateChatMessage(sessionId, data, userToken, { signal } = {}) {
  try {
    return createSSEStream(new URL(`chat/sessions/${sessionId}/messages`, baseUrl).href, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      payload: data,
      signal
    })
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'generateChatMessage stream creation',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

/**
 * Edit a chat message (SSE stream)
 * @param {string} sessionId
 * @param {string} messageId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function editChatMessage(sessionId, messageId, data, userToken, { signal } = {}) {
  try {
    return createSSEStream(new URL(`chat/sessions/${sessionId}/messages/${messageId}`, baseUrl).href, {
      method: "PUT",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      payload: data,
      signal
    })
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'editChatMessage stream creation',
        errorCode: chatError.code,
        sessionId,
        messageId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

/**
 * Regenerate a chat message (SSE stream)
 * @param {string} sessionId
 * @param {string} messageId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function regenerateChatMessage(sessionId, messageId, data, userToken, { signal } = {}) {
  try {
    return createSSEStream(new URL(`chat/sessions/${sessionId}/messages/${messageId}/regenerate`, baseUrl).href, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      payload: data,
      signal
    })
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'regenerateChatMessage stream creation',
        errorCode: chatError.code,
        sessionId,
        messageId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

/**
 * Send agent message (SSE stream)
 * @param {string} sessionId
 * @param {Object} data
 * @param {string} data.query
 * @param {string} data.userId
 * @param {string[]} [data.images]
 * @param {{name: string, content: string}[]} [data.files]
 * @param {import("./utils").AgentModeValue} data.mode
 * @param {string} [data.canvasDocuments]
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function sendAgentQuery(sessionId, data, userToken, { signal } = {}) {
  try {
    const { mode, ...payload } = data
    const baseEndpoint = mode === "deep_research"
      ? new URL("deepresearch/query", baseUrl).href
      : new URL("agent/query", baseUrl).href

    return createSSEStream(baseEndpoint, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      payload: {
        ...payload,
        ...(mode !== "deep_research" ? { mode } : {}),
        sessionId,
      },
      signal
    })
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'sendAgentQuery stream creation',
        errorCode: chatError.code,
        sessionId,
        mode: data.mode,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

/**
 * Generate a chat report (SSE stream)
 * @param {string} sessionId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function generateChatReport(sessionId, data, userToken, { signal } = {}) {
  try {
    return createSSEStream(new URL(`chat/sessions/${sessionId}/reports`, baseUrl).href, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      payload: data,
      signal
    })
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'generateChatReport stream creation',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const UpdateChatReportSchema = z.object({
  report: z.object({}).passthrough()
})

/**
 * @param {string} reportId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function updateChatReport(reportId, data, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/reports/${reportId}`, baseUrl).href, {
      method: "PUT",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return UpdateChatReportSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'updateChatReport API call',
        errorCode: chatError.code,
        reportId,
        data: data ? 'present' : 'missing',
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

/**
 * Get active stream for a session (SSE stream)
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {ReadableStream<StreamEventsPayload>}
 */
function getSessionStream(sessionId, userToken, { signal } = {}) {
  try {
    return createSSEStream(new URL(`chat/sessions/${sessionId}/stream`, baseUrl).href, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${userToken}`,
      },
      signal
    })
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'getSessionStream stream creation',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

/**
 * Cancel active stream for a session
 * @param {string} sessionId
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function cancelSessionStream(sessionId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}/stream`, baseUrl).href, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${userToken}`
      },
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return responseJson.data
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'cancelSessionStream API call',
        errorCode: chatError.code,
        sessionId,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const ExtractFileContentsSchema = z.object({
  content: z.string()
})

/**
 * @param {File} file
 * @param {string} userToken
 */
async function extractFileContents(file, userToken) {
  const formData = new FormData()
  formData.append("file", file)
  try {
    const response = await fetch(new URL("chat/extract-file-contents", baseUrl).href, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
      },
      body: formData,
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return ExtractFileContentsSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'extractFileContents API call',
        errorCode: chatError.code,
        fileName: file?.name,
        fileSize: file?.size,
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}

const AddChatMessageFeedbackSchema = z.object({
  session: z.object({}).passthrough()
})

/**
 * @param {string} sessionId
 * @param {string} messageId
 * @param {Object} data
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function addChatMessageFeedback(sessionId, messageId, data, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`chat/sessions/${sessionId}/messages/${messageId}/feedback`, baseUrl).href, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${userToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
      signal
    })
    const responseJson = await response.json()
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      throw parseBackendError(responseJson, response.status)
    }

    return AddChatMessageFeedbackSchema.parse(responseJson.data)
  } catch (error) {
    const chatError = handleFetchError(error)
    Sentry.captureException(chatError, {
      extra: {
        context: 'addChatMessageFeedback API call',
        errorCode: chatError.code,
        sessionId,
        messageId,
        data: data ? 'present' : 'missing',
        userToken: userToken ? 'present' : 'missing'
      }
    });
    throw chatError
  }
}


// ─── Canvas / Document API ────────────────────────────────────────────────────

/**
 * @param {{ title: string, content: string, sessionId: string }} data
 * @param {string} userToken
 * @param {{ signal?: AbortSignal }} [options]
 */
async function createDocument(data, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL("documents", baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(data),
      signal,
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} userToken
 * @param {{ signal?: AbortSignal }} [options]
 */
async function getDocument(documentId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`documents/${documentId}`, baseUrl).href, {
      headers: { "Authorization": `Bearer ${userToken}` },
      signal,
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} versionId
 * @param {string} userToken
 * @param {{ signal?: AbortSignal }} [options]
 */
async function getDocumentVersion(documentId, versionId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/versions/${versionId}`, baseUrl).href, {
      headers: { "Authorization": `Bearer ${userToken}` },
      signal,
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {{ title?: string, content?: string, sessionId?: string }} data
 * @param {string} userToken
 * @param {{ signal?: AbortSignal }} [options]
 */
async function updateDocument(documentId, data, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`documents/${documentId}`, baseUrl).href, {
      method: "PATCH",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(data),
      signal,
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} userToken
 * @param {{ signal?: AbortSignal }} [options]
 */
async function deleteDocument(documentId, userToken, { signal } = {}) {
  try {
    const response = await fetch(new URL(`documents/${documentId}`, baseUrl).href, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${userToken}` },
      signal,
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} diffId
 * @param {{ sessionId?: string }} data
 * @param {string} userToken
 */
async function acceptDocumentDiff(documentId, diffId, data, userToken) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/diffs/${diffId}/accept`, baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} diffId
 * @param {string} userToken
 */
async function rejectDocumentDiff(documentId, diffId, userToken) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/diffs/${diffId}/reject`, baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {{ sessionId?: string }} data
 * @param {string} userToken
 */
async function acceptAllDocumentDiffs(documentId, data, userToken) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/diffs/accept-all`, baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} userToken
 */
async function rejectAllDocumentDiffs(documentId, userToken) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/diffs/reject-all`, baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} userToken
 */
async function undoDocumentVersion(documentId, userToken) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/undo`, baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {string} userToken
 */
async function redoDocumentVersion(documentId, userToken) {
  try {
    const response = await fetch(new URL(`documents/${documentId}/redo`, baseUrl).href, {
      method: "POST",
      headers: { "Authorization": `Bearer ${userToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
    const json = await response.json()
    if (!response.ok) { dispatchSessionTimeoutIfUnauthorized(response.status); throw parseBackendError(json, response.status) }
    return json
  } catch (error) { throw handleFetchError(error) }
}

/**
 * @param {string} documentId
 * @param {"pdf"|"latex"} format
 * @param {string} userToken
 * @param {{ versionId?: string }} [options]
 */
async function exportDocument(documentId, format, userToken, { versionId } = {}) {
  try {
    const url = new URL(`documents/${documentId}/export`, baseUrl)
    url.searchParams.set("format", format)
    if (versionId) url.searchParams.set("versionId", versionId)

    const response = await fetch(url.href, {
      headers: { "Authorization": `Bearer ${userToken}` },
    })
    if (!response.ok) {
      dispatchSessionTimeoutIfUnauthorized(response.status)
      const json = await response.json()
      throw parseBackendError(json, response.status)
    }
    const blob = await response.blob()
    const contentDisposition = response.headers.get("Content-Disposition") ?? ""
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch?.[1] ?? `document.${format === "pdf" ? "pdf" : "tex"}`
    return { blob, filename }
  } catch (error) { throw handleFetchError(error) }
}

export {
  getChatResources,
  getChatSessions,
  getChatSession,
  getChatSessionMessages,
  getChatProjects,
  getChatSessionReports,
  getChatSessionImages,
  createChatSession,
  createChatProject,
  deleteChatProject,
  updateChatSession,
  deleteChatSession,
  generateChatMessage,
  editChatMessage,
  regenerateChatMessage,
  sendAgentQuery,
  generateChatReport,
  updateChatReport,
  getSessionStream,
  cancelSessionStream,
  extractFileContents,
  addChatMessageFeedback,

  // Canvas
  createDocument,
  getDocument,
  getDocumentVersion,
  updateDocument,
  deleteDocument,
  acceptDocumentDiff,
  rejectDocumentDiff,
  acceptAllDocumentDiffs,
  rejectAllDocumentDiffs,
  undoDocumentVersion,
  redoDocumentVersion,
  exportDocument,

  StreamEvents,

  StreamStartedEventDataSchema,
  StreamContentEventDataSchema,
  StreamStructuredContentEventDataSchema,
  StreamEndedEventDataSchema,
  StreamEventsSchemaMap,

  createSSEStream,
  SSEStreamDisconnectedError,

  // Export error handling utilities
  ErrorCodes,
  ChatProviderError,
}
